import {TFunction} from 'react-i18next';
import Excel, {RichText} from 'exceljs';
import {
	addHTMLImagesAsLinks,
	addXLTable,
	createZIPXL,
	IArrayRoles,
	renderArrayXL,
	renderDateXL,
	renderHeaderXL,
	renderHTMLStringXL,
	renderRichtText,
	renderStringArrayXL,
} from 'components/InfinityList/Excel/ExcelExportCommon';
import {EntityListColumn} from '../EntityList';
import {
	renderRegulationNumberXL,
	renderRegulationVersionXL,
} from 'components/InfinityList/Excel/XLRequirementsPage';

interface IRequirementDetailPage {
	(
		entityListItems: any[],
		arrRoles: IArrayRoles,
		pageDetails: any,
		columns: EntityListColumn[],
		t: TFunction,
	): Promise<void>;
}

export const handleClickBaslineCompare: IRequirementDetailPage = async (
	entityListItems,
	arrRoles,
	pageDetails,
	columns,
	t,
) => {
	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];
	const arrTables: string[] = [];

	const tempDivElement = document.createElement('div');

	const workbook = new Excel.Workbook();
	const arrColWidth: number[] = [
		...new Array(2).fill(40),
		...new Array(3).fill(25),
		40,
		...new Array(2).fill(25),
		...new Array(2).fill(40),
		...new Array(5).fill(35),
		80,
		...new Array(8).fill(25),
	];
	const tOptionsBaselines = {ns: 'features/VehicleProjects/baselineChanges'};

	const tOptions = {
		ns: 'features/vehicleprojects',
		keyPrefix: 'AssignedRequirementsFields',
	};

	const tOptionsRelevanz = {
		ns: 'features/vehicleprojects',
		keyPrefix: 'ProjectRelevantFields',
	};

	const tOptionsCompare = {
		ns: 'features/VehicleProjects/baselineChanges',
		keyPrefix: 'ComparisonResultStatus',
	};

	const getHeading = (rawHeading: string) => {
		return t(rawHeading, tOptionsBaselines);
	};

	const strPageName = getHeading('BaselineCompare');
	const sheet = workbook.addWorksheet('TRACE_' + strPageName);

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		getHeading('BaselineOld'),
		pageDetails.b1Name,
	);
	row.getCell('C').value = renderRichtText(
		getHeading('BaselineNew'),
		pageDetails.b2Name,
	);

	const columnNames = columns.map(x => x.name);

	const columnsXL = columnNames.map(x => {
		return {name: x, filterButton: true};
	});

	intRowCount++;
	intRowCount++;

	addXLTable('tableCompare', intRowCount, sheet, columnsXL);

	const table = sheet.getTable('tableCompare');

	intRowCount++;

	if (entityListItems.length === 0) {
		table.addRow(new Array(columnNames.length).fill(''));
	}
	for (let ii = 0; ii < entityListItems.length; ii++) {
		const iComparedReq = entityListItems[ii];

		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		row.getCell('H').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};

		table.addRow([
			t(iComparedReq.comparisonResult ?? '', tOptionsCompare),
			iComparedReq.isProjectRelevant
				? t('Yes', tOptionsRelevanz)
				: t('No', tOptionsRelevanz),
			iComparedReq.projectRelevantComments ?? '',
			iComparedReq.addedAutomatically
				? t('true', tOptions)
				: t('false', tOptions),
			iComparedReq.addReason === 'AutomaticAssignment'
				? t('AutomaticAssignment', tOptions)
				: iComparedReq.addReason,
			iComparedReq.name,
			{
				text: iComparedReq.requirementId,
				hyperlink: window.location.origin + '/requirements/' + iComparedReq.id,
			},
			iComparedReq.version ?? 'V1.0',
			renderStringArrayXL(renderRegulationNumberXL(iComparedReq.regulations)),
			renderStringArrayXL(
				renderRegulationVersionXL(iComparedReq.regulatoryDocuments),
			),
			renderArrayXL(iComparedReq.markets),
			renderArrayXL(iComparedReq.vexClusters),
			renderDateXL(iComparedReq.dateEffective),
			renderDateXL(iComparedReq.dateEffective),
			t(iComparedReq.status ?? '', {
				ns: 'common/enums',
				keyPrefix: 'RequirementStatus',
			}),
			renderHTMLStringXL('', iComparedReq.definition),
			renderArrayXL(iComparedReq.systemLevels),

			renderArrayXL(iComparedReq.keywords),
			renderArrayXL(iComparedReq.tags),
			iComparedReq.createdBy.name,
			renderDateXL(iComparedReq.modifiedAt),
			t(iComparedReq.category, {
				ns: 'common/enums',
				keyPrefix: 'RequirementCategory',
			}),
		]);

		tempDivElement.innerHTML = iComparedReq.definition
			? '<div>' + iComparedReq.definition + '</div>'
			: '<div></div>';

		intRowCount++;

		intRowCount = addHTMLImagesAsLinks(
			intRowCount,
			iComparedReq.requirementId as string,
			arrImagesName,
			arrImagesUri,
			'Q',
			15,
			tempDivElement,
			sheet,
			table,
			t,
		);
	}

	table.commit();

	const strZipName = strPageName;
	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		[],
	);
};
